function normalizePathname(pathname) {
  if (!pathname) return '/'
  if (pathname === '/') return '/'
  return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname
}

function isSameOriginUrl(href) {
  try {
    const u = new URL(href, location.href)
    return u.origin === location.origin
  } catch {
    return false
  }
}

function getAnchorFromEvent(event) {
  const target = event.target
  if (!(target instanceof Element)) return null
  return target.closest('a[href]')
}

async function parsePage(url) {
  const res = await fetch(url, {
    method: 'GET',
    credentials: 'same-origin',
    headers: {
      'X-TBBC-InPage': '1',
    },
  })
  if (!res.ok) {
    throw new Error(`Failed to fetch ${url}: ${res.status}`)
  }
  const html = await res.text()
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return doc
}

function extractSwapParts(doc) {
  const view = doc.querySelector('#tbbc-view')
  const styles = doc.querySelector('#tbbc-page-styles')
  return { view, styles, title: doc.title, lang: doc.documentElement?.lang }
}

export function initInPageNav(router) {
  if (globalThis.__tbbcInPageNavInit) return
  globalThis.__tbbcInPageNavInit = true

  const allowed = new Set(['/', '/browse'])

  const swapTo = async (pathname) => {
    const normalized = normalizePathname(pathname)
    if (!allowed.has(normalized)) return

    const currentView = document.querySelector('#tbbc-view')
    const currentStyles = document.querySelector('#tbbc-page-styles')
    if (!currentView || !currentStyles) {
      // Not in a swappable page (or markup mismatch) → let browser handle it.
      location.assign(normalized)
      return
    }

    const url = new URL(normalized, location.origin)

    // Avoid stale HTML/CSS when the server/template changes (common during dev).
    // These pages are small and only 2 routes are swappable, so re-fetching is ok.
    const doc = await parsePage(url.toString())
    const parts = extractSwapParts(doc)

    if (!parts.view || !parts.styles) {
      location.assign(normalized)
      return
    }

    const doSwap = () => {
      // title/lang
      if (typeof parts.title === 'string') document.title = parts.title
      if (parts.lang) document.documentElement.lang = parts.lang

      // styles
      currentStyles.textContent = parts.styles.textContent || ''

      // view
      const importedView = document.importNode(parts.view, true)
      currentView.replaceWith(importedView)
    }

    try {
      if (typeof document.startViewTransition === 'function') {
        const vt = document.startViewTransition(doSwap)
        // If available, wait until the new DOM is ready before running page init.
        await (vt?.finished || Promise.resolve())
      } else {
        doSwap()
      }
    } catch {
      // Worst case: still update DOM without animation.
      doSwap()
    }

    // UX: reset scroll for these top-level pages
    window.scrollTo(0, 0)

    // Run page init for the new location (Router uses location.pathname)
    try {
      router?.onDOMContentLoaded?.()
    } catch {
      // ignore
    }
  }

  document.addEventListener(
    'click',
    (event) => {
      if (event.defaultPrevented) return
      if (event.button !== 0) return
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

      const a = getAnchorFromEvent(event)
      if (!a) return

      if (a.target && a.target !== '_self') return
      if (a.hasAttribute('download')) return

      const href = a.getAttribute('href')
      if (!href) return
      if (!isSameOriginUrl(href)) return

      const u = new URL(href, location.href)
      const next = normalizePathname(u.pathname)
      const current = normalizePathname(location.pathname)

      if (!allowed.has(next)) return
      if (next === current) return

      event.preventDefault()

      history.pushState({ __tbbc: true }, '', next)
      swapTo(next).catch(() => {
        location.assign(next)
      })
    },
    true
  )

  window.addEventListener('popstate', () => {
    const next = normalizePathname(location.pathname)
    if (!allowed.has(next)) return
    swapTo(next).catch(() => {
      location.assign(next)
    })
  })
}
